import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  TrendingUp, Users, UserPlus, Stethoscope, CalendarIcon, X, AlertCircle, RefreshCw, ChevronDown, Filter,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { DIRECT_SOURCE_LABEL } from '@/hooks/useUtmAttributionAdmin';
import {
  filterAcquisitionRows, sumAcquisition, dimensionOptions, DIRECT_KEY, NONE_KEY, EMPTY_FILTERS,
  type AcquisitionDimension, type AcquisitionFilters,
} from '@/lib/acquisitionFilters';

interface AcquisitionRow {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  is_direct: boolean;
  visitors: number;
  signups: number;
  diagnostics: number;
}

/** null cuando visitors === 0 (se renderiza como '—'). */
export function conversionRate(signups: number, visitors: number): number | null {
  return visitors > 0 ? signups / visitors : null;
}

function useAcquisitionSummary(from: Date | undefined, to: Date | undefined) {
  return useQuery({
    queryKey: ['utm-acquisition-summary', from?.toISOString() ?? null, to?.toISOString() ?? null],
    queryFn: async (): Promise<AcquisitionRow[]> => {
      const { data, error } = await (supabase as any).rpc('admin_utm_acquisition_summary', {
        _from: from ? from.toISOString() : null,
        _to: to ? to.toISOString() : null,
      });
      if (error) throw error;
      return (data ?? []) as AcquisitionRow[];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

const DIMENSION_LABEL: Record<AcquisitionDimension, string> = {
  source: 'Fuente',
  medium: 'Medio',
  campaign: 'Campaña',
};

function optionLabel(value: string): string {
  if (value === DIRECT_KEY) return DIRECT_SOURCE_LABEL;
  if (value === NONE_KEY) return '(sin valor)';
  return value;
}

/** Selector múltiple con búsqueda para una dimensión UTM. */
function DimensionFilter({
  dim, options, selected, onChange,
}: {
  dim: AcquisitionDimension;
  options: { value: string; signups: number }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const label = DIMENSION_LABEL[dim];
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const summary =
    selected.length === 0 ? `Todas` :
    selected.length === 1 ? optionLabel(selected[0]) :
    `${selected.length} seleccionadas`;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn('w-full md:w-[220px] justify-between font-normal', selected.length === 0 && 'text-muted-foreground')}
          >
            <span className="truncate">{summary}</span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0 bg-background border border-border z-50" align="start">
          <Command>
            <CommandInput placeholder={`Buscar ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>Sin resultados</CommandEmpty>
              <CommandGroup>
                {options.map(o => (
                  <CommandItem key={o.value} value={optionLabel(o.value)} onSelect={() => toggle(o.value)} className="gap-2">
                    <Checkbox checked={selected.includes(o.value)} className="pointer-events-none" />
                    <span className="flex-1 truncate">{optionLabel(o.value)}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{o.signups}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {selected.length > 0 && (
            <div className="border-t p-1">
              <Button variant="ghost" size="sm" className="w-full" onClick={() => onChange([])}>
                Limpiar {label.toLowerCase()}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function AcquisitionDashboardPage() {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const [filters, setFilters] = useState<AcquisitionFilters>(EMPTY_FILTERS);

  const { data, isLoading, isError, refetch } = useAcquisitionSummary(dateFrom, dateTo);
  const allRows = data ?? [];
  // Filtros por Fuente / Medio / Campaña: las tarjetas y la tabla suman solo la selección.
  const rows = useMemo(() => filterAcquisitionRows(allRows, filters), [allRows, filters]);
  const totals = sumAcquisition(rows);
  const hasDimFilters = filters.source.length + filters.medium.length + filters.campaign.length > 0;
  const setDim = (dim: AcquisitionDimension) => (values: string[]) =>
    setFilters(prev => ({ ...prev, [dim]: values }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-primary" />
            Dashboard de Adquisición
          </h1>
          <p className="text-muted-foreground mt-1">
            Visitas, registros y diagnósticos por fuente, medio y campaña
          </p>
        </div>
      </div>

      {/* Filtro de rango de fechas */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full md:w-[160px] justify-start text-left font-normal',
                    !dateFrom && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background border border-border z-50" align="start">
                <CalendarComponent mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus locale={es} />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full md:w-[160px] justify-start text-left font-normal',
                    !dateTo && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background border border-border z-50" align="start">
                <CalendarComponent mode="single" selected={dateTo} onSelect={setDateTo} initialFocus locale={es} />
              </PopoverContent>
            </Popover>

            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="icon" onClick={() => { setDateFrom(undefined); setDateTo(undefined); }}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-4 mt-4">
            {(['source', 'medium', 'campaign'] as AcquisitionDimension[]).map(dim => (
              <DimensionFilter
                key={dim}
                dim={dim}
                options={dimensionOptions(allRows, dim, filters)}
                selected={filters[dim]}
                onChange={setDim(dim)}
              />
            ))}
            {hasDimFilters && (
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => setFilters(EMPTY_FILTERS)}>
                <X className="h-4 w-4" /> Limpiar filtros
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Los valores UTM se guardan en minúsculas (ej. utm_campaign=PRUEBA aparece como “prueba”).
          </p>
        </CardContent>
      </Card>

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No se pudieron cargar las métricas de adquisición</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al consultar el resumen de adquisición.</span>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4" /> Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-72" />
        </div>
      ) : (
        <>
          {/* Tarjetas de totales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Visitantes</p>
                    <p className="text-4xl font-bold text-foreground">{totals.visitors}</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Registros</p>
                    <p className="text-4xl font-bold text-foreground">{totals.signups}</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center">
                    <UserPlus className="w-7 h-7 text-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Diagnósticos completados</p>
                    <p className="text-4xl font-bold text-foreground">{totals.diagnostics}</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
                    <Stethoscope className="w-7 h-7 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla de agrupaciones */}
          <Card>
            <CardHeader>
              <CardTitle>Fuentes de adquisición</CardTitle>
              <CardDescription>
                Agrupado por Fuente, Medio y Campaña — ordenado por número de registros
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Fuente</TableHead>
                      <TableHead>Medio</TableHead>
                      <TableHead>Campaña</TableHead>
                      <TableHead className="text-right">Visitantes</TableHead>
                      <TableHead className="text-right">Registros</TableHead>
                      <TableHead className="text-right">Diagnósticos</TableHead>
                      <TableHead className="text-right">Conversión</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                          {hasDimFilters
                            ? 'No hay datos para los filtros seleccionados'
                            : 'No hay datos de adquisición para el rango seleccionado'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => {
                        const rate = conversionRate(row.signups, row.visitors);
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              {row.is_direct ? (
                                <Badge variant="outline">{DIRECT_SOURCE_LABEL}</Badge>
                              ) : (
                                <Badge variant="outline">{row.utm_source || '—'}</Badge>
                              )}
                            </TableCell>
                            <TableCell>{row.is_direct ? '—' : (row.utm_medium || '—')}</TableCell>
                            <TableCell>{row.is_direct ? '—' : (row.utm_campaign || '—')}</TableCell>
                            <TableCell className="text-right">{row.visitors}</TableCell>
                            <TableCell className="text-right">{row.signups}</TableCell>
                            <TableCell className="text-right">{row.diagnostics}</TableCell>
                            <TableCell className="text-right">
                              {rate === null ? '—' : `${(rate * 100).toFixed(1)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
